import * as GoalAddPage from "@/pages/GoalAddPage";
import * as ProfileEditPage from "@/pages/ProfileEditPage";
import * as ProjectAddPage from "@/pages/ProjectAddPage";
import * as React from "react";

import type { ResourceHubLinkType } from "turboui";
import { useRouteLoaderData } from "react-router";
import { WorkMap } from "turboui";

const UNACCEPTABLE_PATH_CHARACTERS = ["/", "?", "#", "[", "]"];
type MemberType = "team_member" | "outside_collaborator";

export class Paths {
  private companyId: string;

  constructor(opts: { companyId: string }) {
    this.companyId = opts.companyId;
  }

  static lobbyPath() {
    return "/";
  }

  static forgotPasswordPath() {
    return "/forgot-password";
  }

  static newCompanyPath(params?: { redirectTo: string }) {
    return "/new" + encodeUrlParams(params ? { redirect_to: params.redirectTo } : undefined);
  }

  static curatedTemplateEntryPath(templateId: string) {
    return `/templates/${encodeURIComponent(templateId)}/use`;
  }

  curatedTemplateUsePath(templateId: string) {
    return this.createCompanyPath(["templates", encodeURIComponent(templateId), "use"]);
  }

  static companyImportPath() {
    return "/company-import";
  }

  static companyHomePath(companyId: string) {
    return "/" + companyId;
  }

  static companyWorkMapPath(companyId: string) {
    return `/${companyId}/work-map`;
  }

  static inviteJoinPath(token: string) {
    return `/join/${token}`;
  }

  static inviteJoinFullPath(token: string) {
    return `/join/${token}/full`;
  }

  companyPermissionsPath() {
    return this.createCompanyPath(["admin", "permissions"]);
  }

  companyRenamePath() {
    return this.createCompanyPath(["admin", "rename"]);
  }

  companyAdminRestoreSuspendedPeoplePath() {
    return this.createCompanyPath(["admin", "restore-suspended-people"]);
  }

  /**
   * @deprecated The standalone feed page was replaced by Home. Use homePath() for new callers.
   */
  feedPath() {
    return this.homePath();
  }

  homePath() {
    return this.createCompanyPath([]);
  }

  searchPath(query?: string) {
    const path = this.createCompanyPath(["search"]);
    if (!query) return path;

    const searchParams = new URLSearchParams({ q: query });
    return `${path}?${searchParams.toString()}`;
  }

  setupPath() {
    return this.createCompanyPath(["setup"]);
  }

  accountPath() {
    return this.createCompanyPath(["account"]);
  }

  accountSettingsPath() {
    return this.createCompanyPath(["account", "settings"]);
  }

  accountNotificationSettingsPath() {
    return this.createCompanyPath(["account", "settings", "notifications"]);
  }

  accountSecurityPath() {
    return this.createCompanyPath(["account", "security"]);
  }

  accountApiTokensPath() {
    return this.createCompanyPath(["account", "security", "api-tokens"]);
  }

  accountApiTokensUsagePath() {
    return this.createCompanyPath(["account", "security", "api-tokens", "usage"]);
  }

  accountMcpConnectionsPath() {
    return this.createCompanyPath(["account", "security", "mcp-connections"]);
  }

  accountChangePasswordPath() {
    return this.createCompanyPath(["account", "security", "change-password"]);
  }

  accountChangeEmailPath() {
    return this.createCompanyPath(["account", "security", "change-email"]);
  }

  notificationsPath() {
    return this.createCompanyPath(["notifications"]);
  }

  reviewPath() {
    return this.createCompanyPath(["review"]);
  }

  profileEditPath(personId: string, params?: { from: ProfileEditPage.FromLocation }) {
    const path = this.createCompanyPath(["people", personId, "profile", "edit"]);

    if (params?.from) {
      return path + "?from=" + params.from;
    } else {
      return path;
    }
  }

  companyAdminPath() {
    return this.createCompanyPath(["admin"]);
  }

  companyBillingPath(opts?: { checkoutId?: string | null }) {
    const path = this.createCompanyPath(["admin", "billing"]);

    const query = new URLSearchParams();
    if (opts?.checkoutId) query.set("checkout_id", opts.checkoutId);

    const queryString = query.toString();
    return queryString ? `${path}?${queryString}` : path;
  }

  companyBillingPlansPath(opts?: { plan?: string | null; billingPeriod?: string | null }) {
    const path = this.createCompanyPath(["admin", "billing", "plans"]);

    const query = new URLSearchParams();
    if (opts?.plan) query.set("plan", opts.plan);
    if (opts?.billingPeriod) query.set("billing_period", opts.billingPeriod);

    const queryString = query.toString();
    return queryString ? `${path}?${queryString}` : path;
  }

  companyBillingCancelPath() {
    return this.createCompanyPath(["admin", "billing", "cancel"]);
  }

  companyExportPath() {
    return this.createCompanyPath(["admin", "export-company"]);
  }

  companyManagePeoplePath() {
    return this.createCompanyPath(["admin", "manage-people"]);
  }

  companyManageAdminsPath() {
    return this.createCompanyPath(["admin", "manage-admins"]);
  }

  companyManagePeopleAddPeoplePath(opts?: { memberType?: MemberType }) {
    const path = this.createCompanyPath(["admin", "manage-people", "add"]);

    if (opts?.memberType) {
      return `${path}?memberType=${opts.memberType}`;
    }

    return path;
  }

  companyAdminManageTrustedDomainsPath() {
    return this.createCompanyPath(["admin", "manage-trusted-email-domains"]);
  }

  peoplePath() {
    return this.createCompanyPath(["people"]);
  }

  discussionsPath(spaceId: string) {
    return this.createCompanyPath(["spaces", spaceId, "discussions"]);
  }

  discussionDraftsPath(spaceId: string) {
    return this.createCompanyPath(["spaces", spaceId, "discussions", "drafts"]);
  }

  discussionPath(discussionId: string) {
    return this.createCompanyPath(["discussions", discussionId]);
  }

  discussionEditPath(discussionId: string) {
    return this.createCompanyPath(["discussions", discussionId, "edit"]);
  }

  discussionNewPath(spaceId: string) {
    return this.createCompanyPath(["spaces", spaceId, "discussions", "new"]);
  }

  projectPath(
    projectId: string,
    attrs?: {
      tab?: "overview" | "tasks" | "check-ins" | "discussions" | "docs-and-files" | "activity";
      milestoneId?: string;
    },
  ) {
    if (attrs?.milestoneId) {
      return (
        this.createCompanyPath(["projects", projectId]) + `?tab=tasks&milestone=${attrs.milestoneId}&taskDisplay=board`
      );
    }
    if (attrs?.tab) {
      return this.createCompanyPath(["projects", projectId]) + `?tab=${attrs.tab}`;
    }

    return this.createCompanyPath(["projects", projectId]);
  }

  projectMarkdownExportPath(projectId: string) {
    return this.createCompanyPath(["exports", "markdown", "projects", projectId]);
  }

  projectCheckInNewPath(projectId: string) {
    return this.createCompanyPath(["projects", projectId, "check-ins", "new"]);
  }

  projectDiscussionNewPath(projectId: string) {
    return this.createCompanyPath(["projects", projectId, "discussions", "new"]);
  }

  projectDiscussionPath(discussionId: string) {
    return this.createCompanyPath(["project-discussions", discussionId]);
  }

  projectDiscussionEditPath(discussionId: string) {
    return this.createCompanyPath(["project-discussions", discussionId, "edit"]);
  }

  projectActivityPath(activityId: string) {
    return this.createCompanyPath(["project-activities", activityId]);
  }

  projectNewPath({ goalId }: { goalId: string }) {
    return this.createCompanyPath(["projects", "new"]) + "?goalId=" + goalId;
  }

  projectCheckInPath(checkInId: string) {
    return this.createCompanyPath(["project-check-ins", checkInId]);
  }

  projectCheckInEditPath(checkInId: string) {
    return this.createCompanyPath(["project-check-ins", checkInId, "edit"]);
  }

  projectCheckInsPath(projectId: string) {
    return this.createCompanyPath(["projects", projectId]) + "?tab=check-ins";
  }

  projectRetrospectivePath(projectId: string) {
    return this.createCompanyPath(["projects", projectId, "retrospective"]);
  }

  projectRetrospectiveEditPath(projectId: string) {
    return this.createCompanyPath(["projects", projectId, "retrospective", "edit"]);
  }

  projectMilestonePath(milestoneId: string) {
    return this.createCompanyPath(["milestones", milestoneId]);
  }

  pauseProjectPath(projectId: string) {
    return this.createCompanyPath(["projects", projectId, "pause"]);
  }

  resumeProjectPath(projectId: string) {
    return this.createCompanyPath(["projects", projectId, "resume"]);
  }

  orgChartPath() {
    return this.createCompanyPath(["people", "org-chart"]);
  }

  newSpacePath() {
    return this.createCompanyPath(["spaces", "new"]);
  }

  resourceHubPath(resourceHubId: string) {
    return this.createCompanyPath(["resource-hubs", resourceHubId]);
  }

  resourceHubDraftsPath(resourceHubId: string) {
    return this.createCompanyPath(["resource-hubs", resourceHubId, "drafts"]);
  }

  resourceHubFolderPath(folderId: string) {
    return this.createCompanyPath(["folders", folderId]);
  }

  resourceHubFilePath(fileId: string) {
    return this.createCompanyPath(["files", fileId]);
  }

  resourceHubEditFilePath(fileId: string) {
    return this.createCompanyPath(["files", fileId, "edit"]);
  }

  resourceHubDocumentPath(documentId: string, opts?: { inlineThread?: string | null }) {
    const path = this.createCompanyPath(["documents", documentId]);

    return path + encodeUrlParams(opts?.inlineThread ? { inlineThread: opts.inlineThread } : undefined);
  }

  resourceHubDocumentVersionsPath(documentId: string) {
    return this.createCompanyPath(["documents", documentId, "versions"]);
  }

  resourceHubDocumentVersionPath(documentId: string, versionNumber: number) {
    return this.createCompanyPath(["documents", documentId, "versions", String(versionNumber)]);
  }

  resourceHubEditDocumentPath(documentId: string) {
    return this.createCompanyPath(["documents", documentId, "edit"]);
  }

  resourceHubNewDocumentPath(resourceHubId: string, folderId?: string) {
    if (folderId) {
      return this.createCompanyPath(["resource-hubs", resourceHubId, "new-document"]) + "?folderId=" + folderId;
    } else {
      return this.createCompanyPath(["resource-hubs", resourceHubId, "new-document"]);
    }
  }

  resourceHubNewLinkPath(resourceHubId: string, attrs: { folderId?: string; type?: ResourceHubLinkType }) {
    const { folderId, type } = attrs;

    if (folderId && type) {
      return (
        this.createCompanyPath(["resource-hubs", resourceHubId, "new-link"]) + `?folderId=${folderId}&type=${type}`
      );
    } else if (folderId) {
      return this.createCompanyPath(["resource-hubs", resourceHubId, "new-link"]) + "?folderId=" + folderId;
    } else if (type) {
      return this.createCompanyPath(["resource-hubs", resourceHubId, "new-link"]) + "?type=" + type;
    } else {
      return this.createCompanyPath(["resource-hubs", resourceHubId, "new-link"]);
    }
  }

  resourceHubEditLinkPath(linkId: string) {
    return this.createCompanyPath(["links", linkId, "edit"]);
  }

  resourceHubLinkPath(linkId: string) {
    return this.createCompanyPath(["links", linkId]);
  }

  spacePath(spaceId: string) {
    return this.createCompanyPath(["spaces", spaceId]);
  }

  spaceToolsConfigPath(spaceId: string) {
    return this.createCompanyPath(["spaces", spaceId, "tools-config"]);
  }

  spaceKpisPath(spaceId: string) {
    return this.createCompanyPath(["spaces", spaceId, "kpis"]);
  }

  spaceKpiPath(spaceId: string, kpiId: string) {
    return this.createCompanyPath(["spaces", spaceId, "kpis", kpiId]);
  }

  projectTemplatesPath() {
    return this.createCompanyPath(["project-templates"]);
  }

  spaceProjectTemplatesPath(spaceId: string) {
    return this.createCompanyPath(["spaces", spaceId, "project-templates"]);
  }

  newProjectTemplatePath(spaceId: string) {
    return this.spaceProjectTemplatesPath(spaceId) + "?new=true";
  }

  projectTemplateMilestonePath(templateId: string, milestoneId: string) {
    return this.createCompanyPath(["project-templates", templateId, "milestones", milestoneId]);
  }

  projectTemplatePath(
    templateId: string,
    attrs?: { tab?: "overview" | "tasks" | "discussions" | "docs-and-files"; folderId?: string },
  ) {
    const params = new URLSearchParams();

    if (attrs?.folderId) {
      params.set("tab", "docs-and-files");
      params.set("folderId", attrs.folderId);
    } else if (attrs?.tab) {
      params.set("tab", attrs.tab);
    }

    const query = params.toString();
    const path = this.createCompanyPath(["project-templates", templateId]);

    return query ? `${path}?${query}` : path;
  }

  projectTemplateDiscussionNewPath(templateId: string) {
    return this.createCompanyPath(["project-templates", templateId, "discussions", "new"]);
  }

  projectTemplateDiscussionPath(templateId: string, discussionId: string) {
    return this.createCompanyPath(["project-templates", templateId, "discussions", discussionId]);
  }

  projectTemplateDiscussionEditPath(templateId: string, discussionId: string) {
    return this.createCompanyPath(["project-templates", templateId, "discussions", discussionId, "edit"]);
  }

  projectTemplateNewDocumentPath(templateId: string, folderId?: string) {
    if (folderId) {
      return this.createCompanyPath(["project-templates", templateId, "documents", "new"]) + "?folderId=" + folderId;
    } else {
      return this.createCompanyPath(["project-templates", templateId, "documents", "new"]);
    }
  }

  projectTemplateDocumentPath(templateId: string, documentId: string) {
    return this.createCompanyPath(["project-templates", templateId, "documents", documentId]);
  }

  projectTemplateEditDocumentPath(templateId: string, documentId: string) {
    return this.createCompanyPath(["project-templates", templateId, "documents", documentId, "edit"]);
  }

  projectTemplateNewLinkPath(templateId: string, attrs?: { folderId?: string; type?: ResourceHubLinkType }) {
    const folderId = attrs?.folderId;
    const type = attrs?.type;
    const base = this.createCompanyPath(["project-templates", templateId, "links", "new"]);

    if (folderId && type) {
      return base + `?folderId=${folderId}&type=${type}`;
    } else if (folderId) {
      return base + "?folderId=" + folderId;
    } else if (type) {
      return base + "?type=" + type;
    } else {
      return base;
    }
  }

  projectTemplateLinkPath(templateId: string, linkId: string) {
    return this.createCompanyPath(["project-templates", templateId, "links", linkId]);
  }

  projectTemplateEditLinkPath(templateId: string, linkId: string) {
    return this.createCompanyPath(["project-templates", templateId, "links", linkId, "edit"]);
  }

  projectTemplateFilePath(templateId: string, fileId: string) {
    return this.createCompanyPath(["project-templates", templateId, "files", fileId]);
  }

  projectTemplateEditFilePath(templateId: string, fileId: string) {
    return this.createCompanyPath(["project-templates", templateId, "files", fileId, "edit"]);
  }

  spaceKanbanPath(spaceId: string, opts?: { taskId?: string }) {
    if (opts?.taskId) {
      return this.createCompanyPath(["spaces", spaceId, "kanban"]) + "?taskId=" + opts.taskId;
    }
    return this.createCompanyPath(["spaces", spaceId, "kanban"]);
  }

  spaceEditPath(spaceId: string) {
    return this.createCompanyPath(["spaces", spaceId, "edit"]);
  }

  spaceEditGeneralAccessPath(spaceId: string) {
    return this.createCompanyPath(["spaces", spaceId, "edit", "general-access"]);
  }

  spaceDiscussionsPath(spaceId: string) {
    return this.createCompanyPath(["spaces", spaceId, "discussions"]);
  }

  spaceAppearancePath(spaceId: string) {
    return this.createCompanyPath(["spaces", spaceId, "appearance"]);
  }

  spaceAccessManagementPath(spaceId: string) {
    return this.createCompanyPath(["spaces", spaceId, "access"]);
  }

  spaceAddMembersPath(spaceId: string) {
    return this.createCompanyPath(["spaces", spaceId, "add-members"]);
  }

  workMapPath(tab?: WorkMap.Filter) {
    return this.createCompanyPath(["work-map"]) + (tab ? `?tab=${tab}` : "");
  }

  spaceWorkMapPath(spaceId: string, tab?: WorkMap.Filter) {
    return this.createCompanyPath(["spaces", spaceId, "work-map"]) + (tab ? `?tab=${tab}` : "");
  }

  goalClosePath(goalId: string) {
    return this.createCompanyPath(["goals", goalId, "complete"]);
  }

  goalPath(
    goalId: string,
    params?: { tab?: "overview" | "discussions" | "check-ins" | "docs-and-files" | "activity" },
  ) {
    return this.createCompanyPath(["goals", goalId]) + (params?.tab ? "?tab=" + params.tab : "");
  }

  goalAccessManagementPath(goalId: string) {
    return this.createCompanyPath(["goals", goalId, "access"]);
  }

  goalAccessAddPath(goalId: string) {
    return this.createCompanyPath(["goals", goalId, "access", "add"]);
  }

  goalEditPermissionsPath(goalId: string) {
    return this.createCompanyPath(["goals", goalId, "edit", "permissions"]);
  }

  goalMarkdownExportPath(goalId: string) {
    return this.createCompanyPath(["exports", "markdown", "goals", goalId]);
  }

  newGoalPath(params?: GoalAddPage.UrlParams) {
    return this.createCompanyPath(["goals", "new"]) + encodeUrlParams(params);
  }

  spaceNewGoalPath(spaceId: string) {
    return this.createCompanyPath(["spaces", spaceId, "goals", "new"]);
  }

  newProjectPath(params?: ProjectAddPage.UrlParams) {
    return this.createCompanyPath(["projects", "new"]) + encodeUrlParams(params);
  }

  goalNewPath({ parentGoalId }: { parentGoalId: string }) {
    return this.createCompanyPath(["goals", "new"]) + "?parentGoalId=" + parentGoalId;
  }

  goalCheckInNewPath(goalId: string) {
    return this.createCompanyPath(["goals", goalId, "check-ins", "new"]);
  }

  goalCheckInPath(checkInId: string) {
    return this.createCompanyPath(["goal-check-ins", checkInId]);
  }

  newGoalDiscussionPath(goalId: string) {
    return this.createCompanyPath(["goals", goalId, "discussions", "new"]);
  }

  goalDiscussionPath(id: string) {
    return this.createCompanyPath(["goal-activities", id]);
  }

  goalRetrospectivePath(id: string) {
    return this.createCompanyPath(["goal-activities", id]);
  }

  goalDiscussionEditPath(activityId: string) {
    return this.createCompanyPath(["goal-activities", activityId, "edit"]);
  }

  goalActivityPath(activityId: string) {
    return this.createCompanyPath(["goal-activities", activityId]);
  }

  goalReopenPath(goalId: string) {
    return this.createCompanyPath(["goals", goalId, "reopen"]);
  }

  profilePath(personId: string, tab?: string) {
    if (tab) {
      return this.createCompanyPath(["people", personId]) + "?tab=" + tab;
    } else {
      return this.createCompanyPath(["people", personId]);
    }
  }

  projectClosePath(projectId: string) {
    return this.createCompanyPath(["projects", projectId, "close"]);
  }

  taskPath(taskId: string) {
    return this.createCompanyPath(["tasks", taskId]);
  }

  inviteTeamPath() {
    return this.createCompanyPath(["invite-team"]);
  }

  invitePeoplePath() {
    return this.createCompanyPath(["invite-people"]);
  }

  //
  // Private utility methods
  //

  private createCompanyPath(elements: string[]) {
    return this.createPath([this.getCompanyID(), ...elements]);
  }

  private createPath(elements: string[]) {
    Paths.validatePathElements(elements);
    return "/" + elements.join("/");
  }

  private getCompanyID(): string {
    return this.companyId;
  }

  static validatePathElements(elements: string[]) {
    elements.forEach((element) => {
      if (!element) {
        throw new Error("Unsuported path elements: " + JSON.stringify(elements));
      }

      UNACCEPTABLE_PATH_CHARACTERS.forEach((char) => {
        if (element.includes(char)) {
          throw new Error(`Path elements cannot contain ${char}`);
        }
      });
    });
  }
}

export function useOptionalPaths(): Paths | null {
  const data = useRouteLoaderData("companyRoot") as { companyId: string } | undefined;

  return React.useMemo(() => {
    if (!data?.companyId) return null;

    return new Paths({ companyId: data.companyId });
  }, [data?.companyId]);
}

export function usePaths() {
  const paths = useOptionalPaths();

  if (!paths) {
    throw new Error("usePaths must be used within a company route context");
  }

  return paths;
}

type ID = string | null | undefined;

export function compareIds(a: ID, b: ID) {
  if (!a || !b) return false;

  if (isUUID(a) && isUUID(b)) {
    return a === b;
  }

  return idWithoutComments(a) === idWithoutComments(b);
}

export function includesId(idsList: ID[], id: ID) {
  if (!id) return false;

  const ids = idsList
    .filter((id): id is string => Boolean(id))
    .map((id) => {
      if (isUUID(id)) return id;
      return idWithoutComments(id);
    });

  if (isUUID(id)) return ids.includes(id);
  return ids.includes(idWithoutComments(id));
}

function isUUID(id: string) {
  return id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/);
}

export function idWithoutComments(id: string) {
  const parts = id.split("-");
  return parts[parts.length - 1];
}

export function encodeUrlParams(params?: Record<any, any>) {
  if (!params) return "";

  const queryStr = Object.entries(params)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("&");

  return queryStr ? "?" + queryStr : "";
}

/** Return destinations are app-relative; encoded query and fragment values are preserved. */
export function internalReturnPath(value: string | null | undefined): string | null {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return null;
  if (/[\\\u0000-\u0020\u007f]/.test(value)) return null;

  const [pathname = ""] = value.split(/[?#]/, 1);

  if (/%(?:2f|5c|0[0-9a-f]|1[0-9a-f]|7f)/i.test(pathname)) return null;
  return value;
}

export function authPath(path: string, search = window.location.search): string {
  const current = new URLSearchParams(search);
  const params = new URLSearchParams();
  const returnTo = internalReturnPath(current.get("redirect_to"));
  const invite = current.get("invite_token");

  if (returnTo) params.set("redirect_to", returnTo);
  if (invite) params.set("invite_token", invite);

  return params.size ? `${path}?${params}` : path;
}
